import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, Collapse, Stack, Typography,
} from '@mui/material';
import {
  ErrorOutlineRounded as WarnIcon,
  LightbulbOutlined as InfoIcon,
  TrendingUpRounded as OkIcon,
} from '@mui/icons-material';
import HiringService, { CandidateOutcome, Insights } from '../../api/services/hiring.service';
import PageBody from '../../components/ui/PageBody';
import PageHeader from '../../components/ui/PageHeader';
import StatTile from '../../components/ui/StatTile';
import { LoadingState } from '../../components/ui/States';
import { status as statusTokens } from '../../theme';
import {
  DateRange, DateRangeFilter, FilterBar, RangePreset,
} from '../../components/ui/FilterBar';
import { LineChart, Legend as LineLegend, Swatch } from '../payroll/reportCharts';
import ReportsPanel from '../reports/ReportsPanel';
import {
  OUTCOME_LABEL, SOURCE_LABEL, STAGE_LABEL, STAGE_SHORT,
} from './hiringUI';
import {
  BarRow, BarRows, ChartCard, PeopleChips, ToneLegend,
} from './insightCharts';
import { Finding, findings as findingsFor } from './insightFindings';

// What hiring is doing, and what to do about it.
//
// The top of the page is a short ranked list of findings, each a rule over
// the numbers below (insightFindings.ts) with the people behind it one
// click away. Under it, one chart per question, each the form that fits:
//
//   where everybody is      bars per stage, the ones gone quiet in amber
//   what they wait on       bars per next step, ranked, the late in amber
//   how far people get      a funnel: bars per stage, step rates beside
//   where you lose people   bars per stage, split by how it ended
//   where they come from    bars per source, split hired / open / lost
//   over time               lines: added on one row, hired and lost on
//                           another, sharing the same weeks
//
// A funnel that widens is not a funnel: the server counts how far each
// person got, so it can only decrease.

const DAY = 86400000;
// "All time" is a range from before any company existed; the server is
// asked for `from=all` when it is picked.
const ALL_FROM = '2000-01-01';
const isoDay = (t: number) => new Date(t).toISOString().slice(0, 10);
function rangePresets(now = Date.now()): RangePreset[] {
  const today = isoDay(now);
  const back = (days: number) => ({ from: isoDay(now - days * DAY), to: today });
  return [
    { key: '30', label: 'Last 30 days', range: back(30) },
    { key: '90', label: 'Last 90 days', range: back(90) },
    { key: '180', label: 'Last 6 months', range: back(180) },
    { key: '365', label: 'Last 12 months', range: back(365) },
    { key: 'ytd', label: 'Year to date', range: { from: `${today.slice(0, 4)}-01-01`, to: today } },
    { key: 'all', label: 'All time', range: { from: ALL_FROM, to: today } },
  ];
}
const DEFAULT_RANGE = '180';

const LOSS_ORDER = ['unresponsive', 'declined', 'withdrew', 'no_show'] as const satisfies readonly CandidateOutcome[];

const fmt = (iso: string, opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }) => new Date(iso).toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' });

const FINDING_ICON = {
  warn: <WarnIcon fontSize="small" sx={{ color: statusTokens.warn }} />,
  info: <InfoIcon fontSize="small" color="primary" />,
  ok: <OkIcon fontSize="small" sx={{ color: statusTokens.ok }} />,
};

const FindingRow: React.FC<{ f: Finding }> = ({ f }) => {
  const [open, setOpen] = useState(false);
  const count = f.total ?? f.people?.length ?? 0;
  return (
    <Box sx={{ py: 1.25, '& + &': { borderTop: '1px solid', borderColor: 'divider' } }}>
      <Stack direction="row" gap={1.25} alignItems="flex-start">
        <Box sx={{ pt: '2px', display: 'flex' }}>{FINDING_ICON[f.tone]}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{f.title}</Typography>
          <Typography variant="body2" color="text.secondary">{f.action}</Typography>
          {f.people && f.people.length > 0 && (
            <Collapse in={open} unmountOnExit>
              <PeopleChips people={f.people} total={f.total} detail={f.detail} />
            </Collapse>
          )}
        </Box>
        {f.people && f.people.length > 0 && (
          <Button size="small" onClick={() => setOpen((o) => !o)} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            {open ? 'Hide' : `Show ${count === 1 ? 'them' : `all ${count}`}`}
          </Button>
        )}
      </Stack>
    </Box>
  );
};

const HiringInsights: React.FC = () => {
  const presets = useMemo(() => rangePresets(), []);
  const defaultRange = presets.find((p) => p.key === DEFAULT_RANGE)!.range;
  const [range, setRange] = useState<DateRange>(defaultRange);
  const [data, setData] = useState<Insights | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stagePick, setStagePick] = useState<string | null>(null);
  const [blockerPick, setBlockerPick] = useState<string | null>(null);

  const allTime = range.from === ALL_FROM;
  const today = presets[0].range.to;

  useEffect(() => {
    setLoading(true);
    setError(null);
    // A range ending today runs to now; one ending earlier, to the end of that day.
    const to = range.to >= today ? undefined : new Date(Date.parse(`${range.to}T00:00:00Z`) + DAY).toISOString();
    HiringService.insights({ from: allTime ? 'all' : new Date(`${range.from}T00:00:00Z`).toISOString(), to })
      .then(setData)
      .catch((err) => setError(err?.response?.data?.message || 'Could not work those out.'))
      .finally(() => setLoading(false));
  }, [range.from, range.to, allTime, today]);

  // How the comparison window reads in a sentence. None for all time.
  const spanDays = Math.round((Date.parse(range.to) - Date.parse(range.from)) / DAY);
  const period = allTime ? null : `the ${spanDays === 365 ? '12 months' : spanDays === 180 ? '6 months' : `${spanDays} days`} before`;
  const list = useMemo(() => (data ? findingsFor(data, period) : []), [data, period]);

  const compare = (now: number | null, was: number | null, fallback: string, suffix = '') => {
    if (!data || !period || data.previous.entered === 0) return fallback;
    if (was == null) return `none ${period}`;
    if (now === was) return `same as ${period}`;
    return `${was}${suffix} ${period}`;
  };

  const header = (
    <PageHeader
      title="Hiring"
      subtitle={data ? `${fmt(data.window.from, { month: 'short', day: 'numeric', year: 'numeric' })} to ${fmt(data.window.to, { month: 'short', day: 'numeric', year: 'numeric' })}` : undefined}
    >
      <FilterBar
        onReset={() => setRange(defaultRange)}
        canReset={range.from !== defaultRange.from || range.to !== defaultRange.to}
      >
        <DateRangeFilter
          label="Added"
          range={range}
          presets={presets}
          onChange={setRange}
          defaultKey={DEFAULT_RANGE}
          hint="Candidates added between these two days."
        />
      </FilterBar>
    </PageHeader>
  );

  if (!data) {
    return (
      <PageBody fluid>
        <ReportsPanel />
        {header}
        {error && <Alert severity="error">{error}</Alert>}
        {loading && <LoadingState label="Working them out…" />}
      </PageBody>
    );
  }

  // ------------------------------------------------------------ rows

  const pipelineRows: BarRow[] = data.pipeline.map((p) => ({
    key: p.stage,
    label: STAGE_LABEL[p.stage],
    segments: p.stallDays == null
      ? [{ key: 'waiting', label: 'Waiting to start', value: p.count, tone: 'added' }]
      : [
        { key: 'moving', label: 'On pace', value: p.count - p.stalled, tone: 'added' },
        { key: 'stalled', label: `Quiet over ${p.stallDays} days`, value: p.stalled, tone: 'warn' },
      ],
    note: p.count ? `median ${p.medianIdleDays}d idle` : '',
    muted: p.count === 0,
  }));
  const stagePeople = stagePick === 'ready'
    ? { people: data.readyNoStart, total: data.readyNoStartTotal, what: 'with no start date' }
    : stagePick
      ? { people: data.stalled.filter((s) => s.stage === stagePick), total: undefined, what: 'gone quiet' }
      : null;

  const blockers = data.blockers.slice(0, 8);
  const OWED = { us: 'yours', them: 'theirs', third_party: 'outside' } as const;
  const blockerRows: BarRow[] = blockers.map((b) => {
    const owed = (Object.entries(b.owedBy) as [keyof typeof OWED, number][]).sort((x, y) => y[1] - x[1])[0][0];
    return {
      key: b.key,
      label: b.label,
      segments: [
        { key: 'late', label: 'Past due', value: b.overdue, tone: 'warn' },
        { key: 'ok', label: 'Not due yet', value: b.count - b.overdue, tone: 'added' },
      ],
      note: `${OWED[owed]}${b.stage ? ` · ${STAGE_SHORT[b.stage]}` : ''}`,
    };
  });
  const pickedBlocker = blockers.find((b) => b.key === blockerPick) || null;

  // Step rates: of those who reached the stage before, how many got here.
  const steps = data.funnel.map((f, i) => (i === 0 || !data.funnel[i - 1].count
    ? null
    : Math.round((f.count / data.funnel[i - 1].count) * 100)));
  const weakest = steps.reduce<number | null>((w, s, i) => (s != null && data.funnel[i - 1].count >= 5 && (w == null || s < (steps[w] as number)) ? i : w), null);
  const funnelRows: BarRow[] = data.funnel.map((f, i) => ({
    key: f.stage,
    label: STAGE_LABEL[f.stage],
    segments: [{ key: 'reached', label: 'Reached', value: f.count, tone: 'added' }],
    note: steps[i] == null
      ? 'everyone added'
      : (
        <Box component="span" sx={i === weakest ? { color: statusTokens.warn, fontWeight: 700 } : undefined}>
          {steps[i]}% of {STAGE_SHORT[data.funnel[i - 1].stage]}
        </Box>
      ),
  }));

  const lossStages = data.funnel.map((f) => f.stage).filter((s) => data.lostBy[s]);
  const lossRows: BarRow[] = lossStages.map((stage) => {
    const by = data.lostBy[stage] || {};
    const n = LOSS_ORDER.reduce((sum, o) => sum + (by[o] || 0), 0);
    return {
      key: stage,
      label: STAGE_LABEL[stage],
      segments: LOSS_ORDER.map((o) => ({ key: o, label: OUTCOME_LABEL[o], value: by[o] || 0, tone: o })),
      note: `${data.lostCount ? Math.round((n / data.lostCount) * 100) : 0}% of losses`,
    };
  });

  const sourceRows: BarRow[] = data.sources.map((s) => {
    const finished = s.hired + s.lost;
    return {
      key: s.source,
      label: SOURCE_LABEL[s.source] || s.source,
      segments: [
        { key: 'hired', label: 'Hired', value: s.hired, tone: 'hired' },
        { key: 'open', label: 'Still open', value: s.open, tone: 'added' },
        { key: 'lost', label: 'Lost', value: s.lost, tone: 'lost' },
      ],
      note: s.conversion == null ? 'none finished' : finished < 5 ? `${s.conversion}% of ${finished}` : `${s.conversion}% hired`,
    };
  });

  const monthly = data.trend.unit === 'month';
  const slots = data.trend.points.map((p) => ({
    key: p.start,
    label: monthly ? fmt(p.start, { month: 'short' }) : fmt(p.start),
  }));
  const slotName = (w: { key: string; label: string }) => (monthly ? fmt(w.key, { month: 'long', year: 'numeric' }) : `Week of ${w.label}`);
  const anyTrend = data.trend.points.some((p) => p.added || p.hired || p.lost);

  const pair = { display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' }, alignItems: 'start' } as const;

  return (
    <PageBody fluid>
      <ReportsPanel />
      {header}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack gap={2} sx={{ opacity: loading ? 0.55 : 1, transition: 'opacity 150ms' }}>
        {data.cohortIsSmall && (
          <Alert severity="info">
            Only {data.entered} {data.entered === 1 ? 'candidate was' : 'candidates were'} added in this window. Read the rates as counts.
          </Alert>
        )}

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' } }}>
          <StatTile
            label="In the pipeline"
            value={data.openTotal}
            hint={data.stalledTotal ? `${data.stalledTotal} gone quiet` : 'open right now'}
          />
          <StatTile label="Added" value={data.entered} hint={compare(data.entered, data.previous.entered, 'in this window')} />
          <StatTile label="Hired" value={data.hiredCount} hint={compare(data.hiredCount, data.previous.hiredCount, 'from those added')} />
          <StatTile
            label="Hire rate"
            value={data.conversion == null ? null : `${data.conversion}%`}
            hint={compare(data.conversion, data.previous.conversion, `of ${data.hiredCount + data.lostCount} finished`, '%')}
          />
          <StatTile
            label="Days to hire"
            value={data.medianDaysToHire}
            hint={compare(data.medianDaysToHire, data.previous.medianDaysToHire, 'median, added to hired')}
          />
        </Box>

        {list.length > 0 && (
          <Card sx={{ px: 2.5, py: 1.25 }}>
            <Typography sx={{ fontWeight: 700, pt: 1 }}>What stands out</Typography>
            {list.map((f) => <FindingRow key={f.key} f={f} />)}
          </Card>
        )}

        <Box sx={pair}>
          <ChartCard
            title="Where everybody is"
            aside={<ToneLegend items={[{ tone: 'added', label: 'On pace' }, { tone: 'warn', label: 'Gone quiet' }]} />}
          >
            <BarRows
              rows={pipelineRows}
              selected={stagePick}
              onPick={(k) => setStagePick((cur) => (cur === k ? null : k))}
            />
            {stagePeople && (
              stagePeople.people.length
                ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">{STAGE_LABEL[stagePick as keyof typeof STAGE_LABEL]}, {stagePeople.what}</Typography>
                    <PeopleChips people={stagePeople.people} total={stagePeople.total} detail={(p) => (p.idleDays != null ? `${p.idleDays}d` : null)} />
                  </Box>
                )
                : <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Nobody here {stagePeople.what}.</Typography>
            )}
          </ChartCard>

          <ChartCard
            title="What they are waiting on"
            aside={<ToneLegend items={[{ tone: 'warn', label: 'Past due' }, { tone: 'added', label: 'Not due yet' }]} />}
          >
            {blockerRows.length === 0
              ? <Typography variant="body2" color="text.secondary">Nobody in the pipeline is waiting on anything.</Typography>
              : (
                <BarRows
                  rows={blockerRows}
                  labelWidth={170}
                  selected={blockerPick}
                  onPick={(k) => setBlockerPick((cur) => (cur === k ? null : k))}
                />
              )}
            {pickedBlocker && (
              <PeopleChips
                people={pickedBlocker.people}
                total={pickedBlocker.count}
                detail={(p) => (p.overdueDays ? `${p.overdueDays}d late` : null)}
              />
            )}
          </ChartCard>
        </Box>

        <Box sx={pair}>
          <ChartCard title="How far people get" aside={`of ${data.entered} added`}>
            <BarRows rows={funnelRows} max={Math.max(1, data.entered)} />
          </ChartCard>

          <ChartCard
            title="Where you lose people"
            aside={<ToneLegend items={LOSS_ORDER.map((o) => ({ tone: o, label: OUTCOME_LABEL[o] }))} />}
          >
            {lossRows.length === 0
              ? <Typography variant="body2" color="text.secondary">Nobody added in this window has been lost.</Typography>
              : <BarRows rows={lossRows} inlineCounts />}
          </ChartCard>
        </Box>

        <Box sx={pair}>
          <ChartCard
            title="Where they come from"
            aside={<ToneLegend items={[{ tone: 'hired', label: 'Hired' }, { tone: 'added', label: 'Still open' }, { tone: 'lost', label: 'Lost' }]} />}
          >
            {sourceRows.length === 0
              ? <Typography variant="body2" color="text.secondary">Nobody added in this window.</Typography>
              : <BarRows rows={sourceRows} inlineCounts />}
          </ChartCard>

          {/* People added and people finished, on two rows sharing the
              same slots: on one axis the hires line sits flat on zero
              under every import. */}
          <ChartCard title="Over time" aside={monthly ? 'per month' : 'per week'}>
            {!anyTrend
              ? <Typography variant="body2" color="text.secondary">Nothing happened in this window.</Typography>
              : (
                <>
                  <Stack direction="row" alignItems="center"><Swatch series="added" line /><Typography variant="caption" color="text.secondary">added</Typography></Stack>
                  <LineChart
                    bare
                    xLabels={false}
                    zero
                    height={110}
                    weeks={slots}
                    slotName={slotName}
                    series={[{ series: 'added', label: 'added', values: data.trend.points.map((p) => p.added) }]}
                    format={(n) => String(Math.round(n))}
                  />
                  <Box sx={{ mt: 1 }}>
                    <LineLegend line items={[{ series: 'hired', label: 'hired' }, { series: 'lost', label: 'closed without a hire' }]} />
                  </Box>
                  <LineChart
                    bare
                    xLabels
                    zero
                    height={120}
                    weeks={slots}
                    slotName={slotName}
                    series={[
                      { series: 'hired', label: 'hired', values: data.trend.points.map((p) => p.hired) },
                      { series: 'lost', label: 'closed without a hire', values: data.trend.points.map((p) => p.lost) },
                    ]}
                    format={(n) => String(Math.round(n))}
                  />
                </>
              )}
          </ChartCard>
        </Box>
      </Stack>
    </PageBody>
  );
};

export default HiringInsights;
